import { Component, AfterViewInit, ViewChild, ElementRef, Input } from '@angular/core';
import { Engine, EngineSettings } from '@spi/Engine/Engine';
import { CubeMapTexture } from '@spi/Engine/Texture';
import { SkyBox } from '@spi/Engine/Object/SkyBox';
import { HttpClient } from '@angular/common/http';
import { InputHandler, MouseObjectInteraction } from '@spi/Engine/Input';
import { initializeGameActions } from './GameActions';
import { Scene } from '@spi/Engine/Scene';
import { AssetLoader, ColladaAsset, TextureAsset } from '@spi/Engine/AssetLoader';
import { RenderObject } from '@spi/Engine/Object/RenderObject';
import { Overlay, AdditiveBlendOverlay, VolumetricLightOverlay, TextureOverlay } from '@spi/Engine/Object/Overlay';
import { vec3, vec4 } from 'gl-matrix';
import { AnimationState } from '@spi/Engine/Animation/AnimationState';
import { CompositeObjectInteraction, RenderMode } from '@spi/Engine/Object/ObjectManager';
import { Light, LightType } from '@spi/Engine/Light/Light';
import { EntityMovementBehaviour } from '@spi/Engine/Object/EntityMovementBehaviour';
import { InputControlledBehaviour } from '@spi/Engine/Object/InputControlledBehaviour';
import { PropertyAnimation } from '@spi/Common/Animation';
import { WebsocketService } from './websocket.service';
import { DOMContextBuilder } from '@spi/Engine/Context';
import { FreeViewCamera, ViewPort, AttachedCamera, Camera } from '@spi/Engine/View';
import { SquareBuilder, WallBuilder } from '@spi/Engine/GeometryBuilder';
import { GeometryMesh } from '@spi/Engine/Mesh';
import { RenderedTexture } from '@spi/Engine/Texture';
import { DefaultSceneRenderer } from '@spi/Engine/Renderer';
import { Collection } from '@spi/Common/Geometry';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less']
})
export class AppComponent implements AfterViewInit {

  @ViewChild('canvas', { static: true })
  canvas: ElementRef<HTMLCanvasElement>;

  @ViewChild('textLayer', { static: true })
  textLayer: ElementRef<HTMLDivElement>;

  @Input('scene')
  scene: Scene;

  constructor(private http: HttpClient, private socketService: WebsocketService) {

  }

  ngAfterViewInit(): void {

    this.http.get('assets/settings.json').subscribe(async json => {
      let settings: EngineSettings = json as any;

      let viewPort = new ViewPort(settings.viewport.width, settings.viewport.height);

      let contextBuilder = new DOMContextBuilder(viewPort, this.canvas.nativeElement, this.textLayer.nativeElement);

      let primaryCamera = new FreeViewCamera(viewPort);

      let playerCamera = new AttachedCamera(viewPort);

      let engine = new Engine(contextBuilder, primaryCamera);

      // Create the input handler and make sure the input handler receives the key up and key down events.
      let inputHandler = new InputHandler(this.canvas.nativeElement);
      inputHandler.hookEvents();

      inputHandler.setInputActions(initializeGameActions(engine, primaryCamera));
      let mouseInteraction = new MouseObjectInteraction(engine);

      inputHandler.bindKeyMapping(settings.keymap);
      inputHandler.bindMouseEventAdapter(mouseInteraction);

      engine.tickEventManager.registerFrameEvent(() => inputHandler.handleKeyboard());
      engine.tickEventManager.registerTimeEvent(() => inputHandler.handleMouse(), 50);

      let scene = new Scene(engine, inputHandler);


      this.__lightTesting(engine, scene);

      scene.debugManager.enable('fps');

      let cube = new CubeMapTexture({ sourceDirectory: 'assets/textures/miramar' });
      await cube.load(engine.ctx);

      let skybox = new SkyBox(engine.ctx, primaryCamera);
      skybox.cubeTexture = cube;

      scene.objectManager.addObject(skybox, RenderMode.Primary);

      // await scene.downloadScene(`assets/maps/demo/demo-map.scene`);
      await scene.loadScene(`assets/maps/demo/sponza-map.scene`);
      await this.__colladaTesting(engine, scene, playerCamera);

      this.__setupSceneInteraction(engine, scene);

      this.socketService.setup();

      this.scene = scene;
      engine.activateScene(scene);
      engine.start();

      this.__dummyGeo(scene, playerCamera);
    });
  }

  private __dummyGeo(scene: Scene, camera: Camera) {

    scene.objectManager.iterate(RenderMode.Primary, (obj: any) => obj.dynamicLight = true);
    scene.lightManager.ambientColor = vec3.fromValues(0, 0, 0);
    let light = new Light(
      LightType.Point,
      vec3.fromValues(-20, 0, 5),
      vec3.fromValues(1, 1, 1)
      , 0.0005);

    scene.lightManager.lights.push(light);

    // let renderer = new DefaultSceneRenderer();
    // renderer.init(scene.ctx);
    // renderer.renderMode = RenderMode.Occlusion;

    // let textureRenderer = new RenderedTexture();
    // textureRenderer.init(scene.ctx);

    // textureRenderer.renderTexture(renderer, scene, camera);

    // let blendOverlay = new AdditiveBlendOverlay(scene.ctx, textureRenderer);

    // scene.objectManager.addObject(blendOverlay, RenderMode.Primary);

    //scene.ctx.engine.overlayRenderer.overlay=blendOverlay;

    // scene.tickEventManager.registerTimeEvent(() => {
    //   console.log('Rendering');
    // textureRenderer.renderTexture(renderer, scene, scene.ctx.engine.activeCamera);
    // }, 1500);

    //let overlay = new TextureOverlay(scene.ctx, scene.ctx.engine.texture);

    // let volumeLight = new VolumetricLightOverlay(scene.ctx, textureRenderer);
    // scene.objectManager.addObject(volumeLight, RenderMode.Primary);

    //scene.objectManager.addObject(overlay, RenderMode.Primary);

    // let banner = scene.materialManager.getTexture('sponza-img_30');

    // scene.objectManager.iterate(RenderMode.Primary, (obj: any) => {
    //   if(obj.texture == banner) {
    //     obj.texture = scene.ctx.engine.texture;
    //   }
    // });

  }

  private player: RenderObject;
  private otherDude: RenderObject;
  private dudeDied: () => void;

  private async __colladaTesting(engine: Engine, scene: Scene, camera: AttachedCamera) {
    console.log('Loading collada resources');

    let assetLoader = new AssetLoader();
    assetLoader.addAsset(new ColladaAsset(scene, 'assets/models/high/humanoids.spi'));
    assetLoader.addAsset(new ColladaAsset(scene, 'assets/models/high/animals.spi'));
    assetLoader.addAsset(new ColladaAsset(scene, 'assets/models/high/weapons.spi'));

    let attackTarget = (target: RenderObject) => {
      if (vec3.distance(target.position, this.player.position) < 5) {
        var dirPlayer = vec3.subtract(vec3.create(), target.position, this.player.position);
        this.player.rotateTo(dirPlayer);
        this.player.animationState.switchState('Attack');

        var dirOtherDude = vec3.subtract(vec3.create(), this.player.position, target.position);
        target.rotateTo(dirOtherDude);
        target.animationState.switchState('Death');
        if (target == this.otherDude) {
          this.dudeDied();
        }
      }
    };

    await assetLoader.resolveAssets(engine);

    console.log('Loaded collada assets');
    let player = scene.objectFactory.create('stickman', [RenderMode.Primary, RenderMode.Picking, RenderMode.Occlusion]);
    player.rotationAxis = vec3.fromValues(0, 0, 1);
    player.position = vec3.fromValues(0, 0, 0);
    player.animationState = new AnimationState(player);
    player.animationState.switchState('Idle');
    player.baseColor = vec4.fromValues(0.2, 0.9, 0.3, 1);

    player.dynamicLight = true;

    let oldCamera: Camera = null;

    let playerInteraction = new CompositeObjectInteraction();
    playerInteraction.addInteractor({
      onPressRelease: () => {
        if (engine.activeCamera != camera) {
          oldCamera = engine.activeCamera;
          engine.activeCamera = camera;
        } else {
          engine.activeCamera = oldCamera;
        }
      }
    });

    player.interaction = playerInteraction;

    camera.attach(player);

    let text = scene.textManager.createTextElement('player');
    text.linkPosition(player);
    text.offset = vec3.fromValues(0, 0, 8);

    let d1 = scene.objectFactory.create('stickman', [RenderMode.Primary, RenderMode.Picking, RenderMode.Occlusion]);
    d1.position = vec3.fromValues(0, 0, 0);
    d1.setScale(1.0);
    // dagger.animationState = new AnimationState(dagger);
    // dagger.animationState.switchState('Walk');
    d1.baseColor = vec4.fromValues(0, 1, 1, 1);


    //this.player.texture = engine.materialManager.getTexture('wood_planks');
    player.attachments.setAttachment("WeaponHand", d1);

    let otherDude = scene.objectFactory.create('stickman', [RenderMode.Primary, RenderMode.Picking, RenderMode.Occlusion]);
    otherDude.rotationAxis = vec3.fromValues(0, 0, 1);
    otherDude.position = vec3.fromValues(3, 25, 0);
    otherDude.animationState = new AnimationState(otherDude);
    otherDude.animationState.switchState('Idle');
    otherDude.baseColor = vec4.fromValues(0.9, 0.2, 0.3, 1);

    let text1 = scene.textManager.createTextElement('door guard');
    text1.linkPosition(otherDude);
    text1.offset = vec3.fromValues(0, 0, 8);

    let interaction = new CompositeObjectInteraction();
    interaction.addInteractor({
      onPressRelease: () => attackTarget(otherDude)
    });

    otherDude.interaction = interaction;

    this.otherDude = otherDude;
    this.player = player;

  }

  private __lightTesting(engine: Engine, scene: Scene) {
    let randomAnimation = (animation: PropertyAnimation) => {
      animation.animate([
        { 'property': 'position', 'index': 0, 'value': Math.random() * 300 - 150, 'speed': 0.2 + Math.random() * 0.8 },
        { 'property': 'position', 'index': 1, 'value': Math.random() * 300 - 150, 'speed': 0.2 + Math.random() * 0.8 }
      ]);
    };

    let updownAnimation = (animation: PropertyAnimation, up: boolean) => {
      animation.animate([
        { 'property': 'position', 'index': 2, 'value': up ? 15 : -5, 'speed': 0.1 }
      ]);
    };

    let colors = [
      vec3.fromValues(1, 0, 0),
      vec3.fromValues(0, 0, 1),
      vec3.fromValues(0, 1, 0),
      vec3.fromValues(1, 1, 1)
    ];

    // for (let color of colors) {
    //   let light = new Light(LightType.Point, vec3.fromValues(0, 0, 15), color, 0.02);
    //   scene.lightManager.lights.push(light);

    //   randomAnimation(new PropertyAnimation(light, randomAnimation));

    //   let textElement = new TextElement();
    //   textElement.linkPosition(light);
    //   textElement.text = "light";
    //   scene.textManager.addText(textElement);
    // }

    // let light = new Light(LightType.Point, vec3.fromValues(0, 0, 0), vec3.fromValues(1, 1, 1), 0.02);
    // scene.lightManager.lights.push(light);
    // updownAnimation(new PropertyAnimation(light, (anim) => updownAnimation(anim, light.position[2] < 0)), true);
    // let textElement = new TextElement();
    // textElement.linkPosition(light);
    // textElement.text = "updown-light";
    // scene.textManager.addText(textElement);
  }


  private __setupSceneInteraction(engine: Engine, scene: Scene) {
    // let door = scene.geometry.get('shack_door');
    // door.pickable = true;
    // door.interaction = new CompositeObjectInteraction();
    // door.interaction.addInteractor({
    //   onPressDown: () => {
    //   }
    // });

    // let doorOpen = false;
    // scene.gateResolver.setGate('shack_door', () => doorOpen);

    // this.dudeDied = () => {
    //   console.log('guard died');
    //   doorOpen = true;

    //   scene.objectManager.removeObject(door);
    // };

    let navObject = scene.createNavigationAwareObject(this.player, 10);
    navObject.bind(new EntityMovementBehaviour(this.player));
    navObject.bind(new InputControlledBehaviour(scene));

    let light = new Light(LightType.Point, vec3.fromValues(0, 0, 15), vec3.fromValues(1, 1, 1), 0.0002);
    //scene.lightManager.lights.push(light);

    // scene.tickEventManager.registerFrameEvent(() => {
    //   light.position[0] = scene.viewPort.x;
    //   light.position[1] = scene.viewPort.y;
    // });

    // scene.debugManager.enable('navigation-graph')

  }


}
